// Fleet overview — the landing page (doc 03 §3.1).
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api, type EndpointSummary, type Me, type Page } from "../api";
import {
  Badge,
  Empty,
  ErrorNote,
  PageHeader,
  Spinner,
  Stat,
  StatusDot,
  endpointTone,
  formatAgo,
} from "../ui";

export default function Fleet({ me }: { me: Me }) {
  const [query, setQuery] = useState("");
  const [state, setState] = useState("");
  const [onlyProblems, setOnlyProblems] = useState(false);

  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (state) params.set("state", state);

  const { data, isLoading, error } = useQuery<Page<EndpointSummary>>({
    queryKey: ["endpoints", query, state],
    queryFn: () => api.get(`/v1/endpoints?${params}`),
    // Agents check in every 30 s; refreshing faster than that shows nothing new.
    refetchInterval: 30_000,
  });

  const all = data?.items ?? [];
  const rows = onlyProblems
    ? all.filter((e) => !e.online || e.alert_count > 0 || e.on_backup_origin > 0 || e.config_pending)
    : all;

  const counts = {
    total: all.length,
    online: all.filter((e) => e.online).length,
    offline: all.filter((e) => e.state === "active" && !e.online).length,
    onBackup: all.filter((e) => e.on_backup_origin > 0).length,
    pending: all.filter((e) => e.config_pending).length,
  };

  return (
    <>
      <PageHeader
        title="Fleet"
        subtitle={`${counts.total} Outpost${counts.total === 1 ? "" : "s"}`}
        actions={
          me.role !== "reseller" && (
            <Link to="/deploy" className="btn-primary">
              Deploy new Outpost
            </Link>
          )
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Online" value={counts.online} tone="ok" />
        <Stat
          label="Offline"
          value={counts.offline}
          tone={counts.offline ? "crit" : "idle"}
          hint={counts.offline ? "active but not checking in" : "all active Outposts reporting"}
        />
        <Stat
          label="On backup origin"
          value={counts.onBackup}
          tone={counts.onBackup ? "warn" : "idle"}
          hint="channels pulling from the backup DVR"
        />
        <Stat
          label="Config pending"
          value={counts.pending}
          tone={counts.pending ? "info" : "idle"}
          hint="waiting for the next check-in"
        />
        <Stat label="Channels served" value={all.reduce((sum, e) => sum + e.channel_count, 0)} />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          className="input max-w-xs"
          placeholder="Search service tag, site or model…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select className="input max-w-[12rem]" value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">All states</option>
          {["inventory", "provisioned", "active", "suspended", "revoked", "retired"].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-ink-300">
          <input
            type="checkbox"
            className="accent-brand"
            checked={onlyProblems}
            onChange={(e) => setOnlyProblems(e.target.checked)}
          />
          Needs attention
        </label>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <div className="p-4">
            <ErrorNote error={error} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>
            {all.length === 0
              ? "No Outposts yet. Deploy one from a raw Ubuntu server to get started."
              : "Nothing needs attention."}
          </Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Service tag</th>
                  <th>Site</th>
                  {me.role !== "reseller" && <th>Reseller</th>}
                  <th>State</th>
                  <th>Last check-in</th>
                  <th>Channels</th>
                  <th>Link health</th>
                  <th>Config</th>
                  <th>Agent</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((endpoint) => (
                  <Row key={endpoint.id} endpoint={endpoint} showReseller={me.role !== "reseller"} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function Row({ endpoint, showReseller }: { endpoint: EndpointSummary; showReseller: boolean }) {
  const offline = endpoint.state === "active" && !endpoint.online;
  return (
    <tr>
      <td>
        <Link to={`/endpoints/${endpoint.id}`} className="service-tag hover:underline">
          {endpoint.service_tag}
        </Link>
        <div className="text-xs text-ink-400">{endpoint.hw_model || endpoint.id}</div>
      </td>
      <td>
        {endpoint.site_name ?? <span className="text-ink-400">unassigned</span>}
      </td>
      {showReseller && <td className="text-ink-300">{endpoint.reseller_name ?? "—"}</td>}
      <td>
        <StatusDot
          tone={endpointTone(endpoint.state, endpoint.online)}
          label={offline ? "offline" : endpoint.state}
          pulse={offline}
        />
      </td>
      <td className="tabular-nums text-ink-300">{formatAgo(endpoint.seconds_since_checkin)}</td>
      <td className="tabular-nums">{endpoint.channel_count}</td>
      <td>
        <LinkHealth endpoint={endpoint} />
      </td>
      <td className="tabular-nums">
        {endpoint.config_pending ? (
          <Badge tone="info">
            v{endpoint.applied_config_version ?? "—"} → v{endpoint.desired_config_version}
          </Badge>
        ) : (
          <span className="text-ink-400">v{endpoint.applied_config_version ?? "—"}</span>
        )}
      </td>
      <td className="text-ink-300">
        {endpoint.agent_version ?? "—"}
        {endpoint.release_channel !== "stable" && (
          <Badge tone="warn" className="ml-2">
            {endpoint.release_channel}
          </Badge>
        )}
      </td>
      <td className="text-right">
        {endpoint.alert_count > 0 && <Badge tone="crit">{endpoint.alert_count} alert</Badge>}
      </td>
    </tr>
  );
}

function LinkHealth({ endpoint }: { endpoint: EndpointSummary }) {
  if (endpoint.sources_total === 0) return <span className="text-ink-400">—</span>;
  const degraded = endpoint.sources_total - endpoint.sources_ok;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className={degraded ? "text-state-warn" : "text-state-ok"}>
        {endpoint.sources_ok}/{endpoint.sources_total} receiving
      </span>
      {endpoint.on_backup_origin > 0 && <Badge tone="warn">{endpoint.on_backup_origin} on backup</Badge>}
      {endpoint.worst_seg_fetch_ms != null && (
        <span className="tabular-nums text-ink-400">{Math.round(endpoint.worst_seg_fetch_ms)} ms</span>
      )}
    </div>
  );
}
